import React from 'react';
import 'twin.macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import request from '@teetsh/app/src/utils/request';
import { useBillingInfosData } from '@teetsh/app/src/queries/useBillingInfos';
import { useTranslation } from 'react-i18next';
import ButtonLink from '@teetsh/app/src/components/Buttons/Link';
import { FDP_TRASH_KEY } from '@teetsh/app/src/containers/UserSettings/Trash/FichesDePrep/useFichesDePrepTrash';
import { SEQUENCES_LIST_KEY } from '@teetsh/app/src/queries/sequences/useSequencesFolders';
import { SEQUENCE_KEY } from '@teetsh/app/src/queries/sequences/useSequence';
import { LimitReachedErrorResponse } from '@teetsh/app/src/types/errors';

export function useRestoreFicheDePrep() {
  const { notifySuccess, notifyError } = useNotification();
  const queryClient = useQueryClient();
  const billingInfos = useBillingInfosData();
  const { t } = useTranslation(['common', 'userSettings']);
  const { mutateAsync } = useMutation({
    mutationFn: ({
      id,
      sequenceId,
      type,
    }: {
      id: string;
      sequenceId: string;
      type: 'classic' | 'fdp_standalone';
    }) => {
      const path =
        type === 'fdp_standalone'
          ? `/fiches-de-prep/${id}/restore`
          : `/sequences/${sequenceId}/fiches-de-prep/${id}/restore`;
      return request(path, { method: 'PATCH' });
    },
    onSuccess: (_, { id, sequenceId, type }) => {
      queryClient.invalidateQueries({
        queryKey: [FDP_TRASH_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SEQUENCE_KEY, sequenceId],
      });
      notifySuccess({
        title: t('userSettings:restore.messages.success'),
        message: (
          <ButtonLink
            outlined
            intent="success"
            to={
              type === 'fdp_standalone'
                ? `/fiches-de-prep/${id}`
                : `/sequences/${sequenceId}/fiches-de-prep/${id}`
            }
          >
            {t('userSettings:restore.messages.see')}
          </ButtonLink>
        ),
      });
    },
    onError: ({ response }) => {
      if (response?.status === 403 && (response?.data as any)?.requiredPlan) {
        // TODO improve that part (handle !billingInfos)
        const { requiredPlan, limit } =
          response?.data as LimitReachedErrorResponse;
        notifyError({
          title: `${t(
            `common:components.limitReached.description${
              billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
            }`,
            {
              limit,
              itemLabel: t(
                'common:components.limitReached.labels.programmation',
                {
                  count: limit,
                },
              ),
              currentPlanLabel: billingInfos?.currentPlanLabel,
            },
          )}.`,
          message: `${t(
            `common:components.limitReached.upgradeHint${
              billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
            }`,
            {
              requiredPlan,
            },
          )}.`,
        });
        return;
      }

      notifyError({ title: t('userSettings:restore.messages.error') });
    },
  });
  return mutateAsync;
}
